import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { addChild } from '../../services/parentService';
import { LinkStudentByEmailData } from '../../types/parent';

interface LinkChildDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function LinkChildDialog({ open, onClose, onSuccess }: LinkChildDialogProps) {
  const [studentEmail, setStudentEmail] = useState('');
  const [relationshipType, setRelationshipType] = useState<'parent' | 'guardian' | 'teacher' | 'coach'>('parent');
  const [isPrimary, setIsPrimary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setStudentEmail('');
      setRelationshipType('parent');
      setIsPrimary(false);
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!studentEmail || !studentEmail.trim()) {
      setError('Student email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(studentEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const linkData: LinkStudentByEmailData = {
        studentEmail: studentEmail.trim(),
        relationshipType,
        isPrimary
      };

      await addChild(linkData);

      // Success - close dialog and notify parent
      handleClose();
      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link child';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Child Account</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enter the email address of an existing student account to link them as your child.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Student Email Address"
            type="email"
            value={studentEmail}
            onChange={(e) => setStudentEmail(e.target.value)}
            disabled={loading}
            required
            autoFocus
            sx={{ mb: 2 }}
            helperText="The email address the student used to register"
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Relationship Type</InputLabel>
            <Select
              value={relationshipType}
              label="Relationship Type"
              onChange={(e) => setRelationshipType(e.target.value as typeof relationshipType)}
              disabled={loading}
            >
              <MenuItem value="parent">Parent</MenuItem>
              <MenuItem value="guardian">Guardian</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="coach">Coach</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                disabled={loading}
              />
            }
            label="Set as primary contact"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Linking...' : 'Link Child'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default LinkChildDialog;
